import { initApp, StoreSnapshotService } from 'src/app/services/store-snapshot.service';
/* eslint-disable no-magic-numbers */
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CommonModule, DatePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { AuthModule, LogLevel, OidcSecurityService } from 'angular-auth-oidc-client';
import { provideStore, Store } from '@ngxs/store';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { TranslateModule, TranslateLoader, TranslatePipe } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DepartmentOptionsResolver } from './department/department-options.resolver';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { AccessResolver } from './role/access.resolver';
import { UserOptionsResolver } from './shared/user-view/useroptions.resolver';
import { TenantSelectorComponent } from './tenant-selector/tenant-selector.component';
import { AccessContextsResolver } from './role/accessContexts.resolver';
import { RoleResolver } from './role/role.resolver';
import { TenantResolver } from './tenant/tenant.resolver';
import { ErrorInterceptor } from './core/services/error.interceptor';
import { CoreModule } from './core/core.module';
import { TenantUserAccessState } from './tenant-selector/state/tenant-user-access.state';
import { DepartmentState } from './department/state/department.state';
import { CustomFormStatusesResolver } from './custom-forms/custom-form-statuses.resolver';
import { AuthInterceptor } from './core/services/auth.interceptor';
import { MenuListState } from './core/state/menu-list.state';
import { TenantState } from './tenant/state/tenant.state';
import { USE_DATE_TIME_FORMAT } from './core/ccomx-date-formats';
import { UIElementsModule } from './shared/ui-elements/ui-elements.module';
import { DialogContainerComponent } from './shared/dialogs/dialog-container/dialog-container.component';

import { StaticDependenciesService } from './services/static-dependencies.service';
import { AppUpdateService } from './services/app-update.service';
import { HasAccessGuard } from './shared/guards/has-access.guard';
import { TimeZoneInfoInterceptor } from './core/services/timezone-info.interceptor';
import { MaterialModule } from './modules/material.module';
import { MenuPlacementIdState } from './certificate-user/state/menu-placement-id.state';
import { JobPositionGroupState } from './competence/state/job-position-group.state';
import { JobPositionState } from './competence/state/job-position.state';
import { GlobalErrorState } from './core/state/global-error.state';
import { GlobalSearchResultState } from './core/state/global-search.state';
import { MainTableFilterState } from './core/state/main-table-filter.state';
import { MenuTreeStructureState } from './core/state/menu-tree-structure.state';
import { PriviligeState } from './core/state/privileges.state';
import { UiState } from './core/state/ui.state';
import { ShiftState } from './crew-management/state/shifts/shift.state';
import { DepartmentGroupState } from './department/state/department-group.state';
import { DueListState } from './due-list/state/due-list.state';
import { ComponentsStructureState } from './maintenance-admin/state/components-structure.state';
import { JobTypeState } from './maintenance-admin/state/job-type.state';
import { QuantityTypeState } from './quantity-type/state/quantity-type.state';
import { RunTimeState } from './run-time/state/run-time.state';
import { CompetenceMatrixState } from './shared/competence-matrix/competence-matrix.state';
import { UserState } from './shared/user-view/state/user.state';
import { HashtagState } from './hashtag/state/hashtag.state';
import { ModuleState } from './core/state/module.state';
import { AlertState } from './alert/state/alert.state';
import { AttachmentState } from './shared/attachments/state/attachments.state';
import { ProductOwnerState } from './product-owner/state/product-owner.state';
import { DepartmentCheckInterceptor } from './core/services/department-check.interceptor';
import { CompetenceRolesState } from './competence/state/competence-roles.state';
import { AppendDomainPipe } from './shared/pipes/append-domain-name.pipe';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [AppComponent, TenantSelectorComponent],
  exports: [TranslatePipe],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    CoreModule,
    DialogContainerComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    HammerModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: false,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AuthModule.forRoot({
      config: {
        authority: environment.authUrl,
        redirectUrl: window.location.origin,
        postLoginRoute: '/',
        postLogoutRedirectUri: window.location.origin,
        clientId: 'frontend',
        scope: 'openid profile email backendScope offline_access',
        responseType: 'code',
        silentRenew: true,
        renewTimeBeforeTokenExpiresInSeconds: 10,
        useRefreshToken: true,
        logLevel: LogLevel.None,
        maxIdTokenIatOffsetAllowedInSeconds: 60 * 60 + 15 * 60, // One hour and fifteen minutes.
      },
    }),
    UIElementsModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: DepartmentCheckInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimeZoneInfoInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: { strict: true, useUtc: false },
    },
    provideAppInitializer(() => {
      const initializerFn = initApp(
        inject(OidcSecurityService),
        inject(Store),
        inject(StoreSnapshotService)
      );
      return initializerFn();
    }),
    { provide: USE_DATE_TIME_FORMAT, useValue: false },
    { provide: MAT_TABS_CONFIG, useValue: { animationDuration: '0ms' } },
    provideStore(
      [
        AlertState,
        AttachmentState,
        CompetenceMatrixState,
        CompetenceRolesState,
        ComponentsStructureState,
        DepartmentGroupState,
        DepartmentState,
        DueListState,
        GlobalErrorState,
        GlobalSearchResultState,
        HashtagState,
        JobPositionGroupState,
        JobPositionState,
        JobTypeState,
        MainTableFilterState,
        MenuListState,
        MenuPlacementIdState,
        MenuTreeStructureState,
        ModuleState,
        PriviligeState,
        QuantityTypeState,
        ProductOwnerState,
        RunTimeState,
        ShiftState,
        TenantState,
        TenantUserAccessState,
        UiState,
        UserState,
      ],
      {
        developmentMode: !environment.production,
      },
      withNgxsReduxDevtoolsPlugin({ disabled: environment.production })
    ),
    AccessContextsResolver,
    AccessResolver,
    AppUpdateService,
    CustomFormStatusesResolver,
    DepartmentOptionsResolver,
    HasAccessGuard,
    RoleResolver,
    StoreSnapshotService,
    TenantResolver,
    UserOptionsResolver,
    provideHttpClient(withInterceptorsFromDi()),
    DatePipe,
    AppendDomainPipe,
  ],
})
export class AppModule {
  constructor(
    private appUpdateService: AppUpdateService,
    private staticDependenciesService: StaticDependenciesService
  ) {}
}
